/**
 * LOCAL_MOCK quote exports (MVP 3.1.1 / 3.1.4 / 3.2.2).
 * PDF download uses Quote-{documentNumber}-{revision}.pdf naming; Excel is CSV.
 */
import { Quote, QuoteRequest, QuoteStubFromAttributes, QuotingEntity } from '../models/quoting/quoting.model';
import { QuotingHelper } from '../models/quoting/quoting.helper';
import { downloadTextFile, toCsv } from './presidio-download';
import {
  PresidioQuotePdfLine,
  buildPresidioQuotePdf,
  downloadPdfBytes,
} from './presidio-quote-pdf';
import { downloadQuotesSummaryXlsx } from './presidio-quotes-summary-xlsx';

function asFilterable(quote: QuotingEntity): Partial<Quote & QuoteStubFromAttributes> {
  return quote as Partial<Quote & QuoteStubFromAttributes>;
}

/**
 * MVP 3.1.4 — quote PDF download filename.
 * Example: Quote-2002024400006-01.pdf
 */
export function formatQuotePdfFilename(quoteNumber?: string, revision = '01'): string {
  let body = (quoteNumber || '').trim();
  body = body.replace(/\.pdf$/i, '');
  body = body.replace(/^Quote-/i, '');
  body = body.replace(/^Q-/i, '');

  let rev = revision;
  // Only treat trailing -NN as revision when the body is already a numeric document id.
  const revMatch = body.match(/^(\d+)-(\d{2})$/);
  if (revMatch) {
    body = revMatch[1];
    rev = revMatch[2];
  }

  // Prefer a stable numeric document id when the quote number has digits.
  const digits = body.replace(/\D/g, '');
  const documentNumber =
    digits.length >= 10
      ? digits
      : digits.length > 0
        ? `20020244${digits.padStart(5, '0')}`.slice(0, 13)
        : '2002024400000';

  const revPad = String(rev).replace(/\D/g, '').padStart(2, '0').slice(-2) || '01';
  return `Quote-${documentNumber}-${revPad}.pdf`;
}

/** MVP 3.1.1 — downloads legacy QuotesCustomerPortalExportXL.xlsx (styled). */
export function downloadQuotesSummaryCsv(quotes: QuotingEntity[]): void {
  downloadQuotesSummaryXlsx(quotes);
}

/** MVP 3.1.4 — download a real PDF (not a text stub) named Quote-{n}-{rev}.pdf. */
export function downloadQuotePdfStub(quote: Quote | QuoteRequest): void {
  const q = QuotingHelper.asQuote(quote);
  const filename = formatQuotePdfFilename(quote.number || quote.id);
  const documentNumber = filename.replace(/^Quote-/i, '').replace(/-\d{2}\.pdf$/i, '');

  const lines: PresidioQuotePdfLine[] = [];
  (quote.items || []).forEach((raw, idx) => {
    const item = raw as {
      productSKU?: string;
      lineNumber?: number;
      name?: string;
      manufacturer?: string;
      manufacturerPartNumber?: string;
      quantity?: { value?: number };
      singleBasePrice?: { value?: number };
      total?: { value?: number };
    };
    if (!item.productSKU) {
      return;
    }
    lines.push({
      lineNumber: item.lineNumber ?? idx + 1,
      partNumber: item.manufacturerPartNumber || item.productSKU,
      description: item.name || item.productSKU,
      quantity: item.quantity?.value ?? 1,
      unitPrice: item.singleBasePrice?.value,
      extPrice: item.total?.value,
      manufacturer: item.manufacturer,
    });
  });

  const notes = [q.sellerComment, q.salesNotes, q.poNumber ? `PO: ${q.poNumber}` : '', q.orderNumber ? `Order: ${q.orderNumber}` : '']
    .filter(Boolean)
    .join(' · ');

  const bytes = buildPresidioQuotePdf({
    quoteNumber: documentNumber,
    title: quote.displayName || `Quote ${quote.number}`,
    sourceLabel: 'Quote detail',
    createdAt: quote.creationDate ? new Date(quote.creationDate) : new Date(),
    accountName: q.affiliatedCompany,
    currency: quote.total?.currency || 'USD',
    lines,
    subtotal: quote.total?.value,
    notes: notes || undefined,
    customerNumber: q.affiliatedCompany,
    customerPo: q.poNumber,
    accountManager: q.accountManager,
    paymentTerms: 'Net 30',
  });
  downloadPdfBytes(filename, bytes);
}

export function downloadQuotePartsCsv(quote: Quote | QuoteRequest): void {
  const rows = (quote.items || [])
    .map((raw, idx) => {
      const item = raw as {
        productSKU?: string;
        lineNumber?: number;
        name?: string;
        manufacturer?: string;
        lineType?: string;
        quantity?: { value?: number };
        singleBasePrice?: { value?: number };
        total?: { value?: number };
        recurringStart?: string;
        recurringEnd?: string;
        months?: number;
      };
      if (!item.productSKU) {
        return null;
      }
      return {
        line_number: item.lineNumber ?? idx + 1,
        sku: item.productSKU,
        name: item.name || '',
        manufacturer: item.manufacturer || '',
        line_type: item.lineType || 'Hardware',
        quantity: item.quantity?.value ?? 1,
        unit_price: item.singleBasePrice?.value ?? '',
        ext_price: item.total?.value ?? '',
        recurring_start: item.recurringStart || '',
        recurring_end: item.recurringEnd || '',
        months: item.months ?? '',
      };
    })
    .filter((row): row is NonNullable<typeof row> => !!row);
  downloadTextFile(`${quote.number || quote.id}-parts.csv`, toCsv(rows) || 'line_number\n', 'text/csv');
}

export type QuoteHubSort = 'date_desc' | 'date_asc' | 'number_asc' | 'number_desc' | 'title_asc';

/** MVP 3.1.2 / FR13.2.3 — Quotes page text search field (Orders-style dropdown). */
export type QuoteHubSearchField =
  | 'quoteNo'
  | 'poNumber'
  | 'orderNumber'
  | 'pricingRequestNumber'
  | 'contact'
  | 'partNumber'
  | 'title'
  | 'contractVehicle';

function quoteSearchHaystack(
  e: Partial<Quote & QuoteStubFromAttributes>,
  state: string,
  field?: QuoteHubSearchField | 'all'
): string {
  const byField: Record<QuoteHubSearchField, unknown> = {
    quoteNo: e.number,
    poNumber: e.poNumber,
    orderNumber: e.orderNumber,
    pricingRequestNumber: e.pricingRequestNumber,
    contact: e.contact,
    partNumber: e.partNumbers,
    title: e.displayName,
    contractVehicle: e.contractVehicle,
  };
  if (field && field !== 'all' && field in byField) {
    return String(byField[field] || '')
      .toLowerCase();
  }
  return [
    e.number,
    e.displayName,
    e.poNumber,
    e.orderNumber,
    e.pricingRequestNumber,
    e.contact,
    e.contractVehicle,
    e.affiliatedCompany,
    e.partNumbers,
    state,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function refinePresidioQuotes(
  quotes: QuotingEntity[],
  opts: {
    search: string;
    /** When set, search only this field (MVP 3.1.2 dropdown). */
    searchField?: QuoteHubSearchField | 'all';
    status: string;
    dateFrom: string;
    dateTo: string;
    /** Affiliated company display name; empty = any. */
    affiliate?: string;
    sort: QuoteHubSort;
  }
): QuotingEntity[] {
  const q = (opts.search || '').trim().toLowerCase();
  const affiliate = (opts.affiliate || '').trim().toLowerCase();
  // Inbound quotes only — exclude DIAS QuoteRequest rows (would show Unknown / no expiration).
  let filtered = quotes.filter(quote => {
    if ((quote as { type?: string }).type !== 'Quote') {
      return false;
    }
    const e = asFilterable(quote);
    const state = QuotingHelper.state(quote);
    if (opts.status && opts.status !== 'all' && state !== opts.status) {
      return false;
    }
    if (affiliate && String(e.affiliatedCompany || '').toLowerCase() !== affiliate) {
      return false;
    }
    if (opts.dateFrom) {
      const from = Date.parse(opts.dateFrom);
      if (!Number.isNaN(from) && (e.creationDate || 0) < from) {
        return false;
      }
    }
    if (opts.dateTo) {
      const to = Date.parse(opts.dateTo);
      if (!Number.isNaN(to) && (e.creationDate || 0) > to + 86_400_000 - 1) {
        return false;
      }
    }
    if (!q) {
      return true;
    }
    return quoteSearchHaystack(e, state, opts.searchField || 'all').includes(q);
  });

  filtered = [...filtered].sort((a, b) => {
    const ae = asFilterable(a);
    const be = asFilterable(b);
    switch (opts.sort) {
      case 'date_asc':
        return (ae.creationDate || 0) - (be.creationDate || 0);
      case 'number_asc':
        return (ae.number || '').localeCompare(be.number || '');
      case 'number_desc':
        return (be.number || '').localeCompare(ae.number || '');
      case 'title_asc':
        return (ae.displayName || '').localeCompare(be.displayName || '');
      case 'date_desc':
      default:
        return (be.creationDate || 0) - (ae.creationDate || 0);
    }
  });
  return filtered;
}
